/**
 * SEO Utilities for RRideStudio
 * Handles metadata generation, structured data, and SEO optimization
 */

// Site configuration
const SITE_CONFIG = {
  name: 'RRideStudio',
  alternateName: 'RRide Moto Studio',
  url: 'https://rridestudio.com',
  description: 'Premium motorcycle accessories, riding gear, LED lights, tank covers, and touring accessories for Indian riders. Shop authentic motorcycle parts and customization products.',
  keywords: ['motorcycle accessories', 'bike accessories', 'riding gear', 'motorcycle LED lights', 'tank covers', 'touring accessories', 'motorcycle parts', 'bike customization'],
  social: {
    facebook: '',
    twitter: '',
    instagram: 'https://www.instagram.com/rridestudio',
    whatsapp: 'https://wa.me/918793678780'
  },
  contact: {
    phone: '+91 8793678780',
    email: 'rridemods@gmail.com'
  },
  logo: '/Logo-removebg-preview.png'
};

/**
 * Generate page title
 */
export const generateTitle = (title, suffix = true) => {
  const cleanTitle = title?.trim() || '';
  // If no title provided, use default site name
  if (!cleanTitle) {
    return SITE_CONFIG.name;
  }
  // If title already contains the brand name, don't add suffix
  if (cleanTitle.toLowerCase().includes('rride') || cleanTitle.toLowerCase().includes('rridestudio')) {
    return cleanTitle;
  }
  // Add suffix for regular pages
  if (suffix) {
    return `${cleanTitle} | RRideStudio`;
  }
  return cleanTitle;
};

/**
 * Generate meta description
 */
export const generateDescription = (description, maxLength = 160) => {
  const cleanDesc = description?.trim() || SITE_CONFIG.description;
  if (cleanDesc.length <= maxLength) {
    return cleanDesc;
  }
  return cleanDesc.substring(0, maxLength - 3) + '...';
};

/**
 * Generate canonical URL
 */
export const generateCanonical = (path) => {
  const cleanPath = path?.replace(/\/+/g, '/') || '/';
  return `${SITE_CONFIG.url}${cleanPath}`;
};

/**
 * Generate product page title
 */
export const generateProductTitle = (product) => {
  if (!product) return generateTitle('Product');
  
  const parts = [product.name];
  
  // Add bike compatibility if available
  if (product.compatibility && product.compatibility.length > 0) {
    const bikes = product.compatibility.slice(0, 2).map(c => 
      `${c.brand} ${c.model}`
    ).join(', ');
    if (bikes) parts.push(`for ${bikes}`);
  }
  
  parts.push('RRideStudio');
  return parts.join(' | ');
};

/**
 * Generate product description
 */
export const generateProductDescription = (product) => {
  if (!product) return SITE_CONFIG.description;
  
  let description = product.description || '';
  
  // If description is too short, generate one
  if (description.length < 50) {
    const parts = [
      `Shop ${product.name}`,
      product.compatibility && product.compatibility.length > 0 
        ? `compatible with ${product.compatibility.slice(0, 2).map(c => c.brand).join(', ')} motorcycles`
        : 'for motorcycles',
      'at RRideStudio. Premium quality motorcycle accessories.'
    ];
    description = parts.join(' ');
  }
  
  return generateDescription(description, 160);
};

/**
 * Generate category page title
 */
export const generateCategoryTitle = (category) => {
  if (!category) return generateTitle('Category');
  return generateTitle(`${category.name} Motorcycle Accessories`);
};

/**
 * Generate category description
 */
export const generateCategoryDescription = (category) => {
  if (!category) return SITE_CONFIG.description;
  
  const desc = category.description || 
    `Shop premium ${category.name.toLowerCase()} motorcycle accessories at RRideStudio. Wide range of ${category.name.toLowerCase()} for all motorcycle models.`;
  
  return generateDescription(desc, 160);
};

/**
 * Generate Open Graph metadata
 */
export const generateOGMetadata = (title, description, image, url) => ({
  'og:title': title || SITE_CONFIG.name,
  'og:description': description || SITE_CONFIG.description,
  'og:image': image || `${SITE_CONFIG.url}${SITE_CONFIG.logo}`,
  'og:url': url || SITE_CONFIG.url,
  'og:type': 'website',
  'og:site_name': SITE_CONFIG.name,
});

/**
 * Generate Twitter Card metadata
 */
export const generateTwitterMetadata = (title, description, image) => ({
  'twitter:card': 'summary_large_image',
  'twitter:title': title || SITE_CONFIG.name,
  'twitter:description': description || SITE_CONFIG.description,
  'twitter:image': image || `${SITE_CONFIG.url}${SITE_CONFIG.logo}`,
});

/**
 * Generate Product structured data (JSON-LD)
 */
export const generateProductSchema = (product, productUrl) => {
  if (!product) return null;
  
  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: product.name,
    description: product.description || generateProductDescription(product),
    image: product.images || [],
    sku: product.product_code,
    brand: {
      '@type': 'Brand',
      name: 'RRideStudio'
    },
    offers: {
      '@type': 'Offer',
      price: product.price,
      priceCurrency: 'INR',
      availability: product.stock > 0 
        ? 'https://schema.org/InStock' 
        : 'https://schema.org/OutOfStock',
      url: productUrl,
      seller: {
        '@type': 'Organization',
        name: SITE_CONFIG.name
      }
    }
  };
  
  // Add category if available
  if (product.spare_category_name) {
    schema.category = product.spare_category_name;
  }
  
  // Add aggregate rating if reviews exist (future implementation)
  // if (product.rating && product.reviewCount) {
  //   schema.aggregateRating = {
  //     '@type': 'AggregateRating',
  //     ratingValue: product.rating,
  //     reviewCount: product.reviewCount
  //   };
  // }
  
  return JSON.stringify(schema);
};

/**
 * Generate Organization structured data (JSON-LD)
 */
export const generateOrganizationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_CONFIG.alternateName,
    alternateName: SITE_CONFIG.name,
    url: SITE_CONFIG.url,
    logo: `${SITE_CONFIG.url}${SITE_CONFIG.logo}`,
    description: SITE_CONFIG.description,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: SITE_CONFIG.contact.phone,
      contactType: 'customer service',
      email: SITE_CONFIG.contact.email
    },
    sameAs: [
      SITE_CONFIG.social.instagram,
      SITE_CONFIG.social.whatsapp
    ].filter(Boolean)
  };
  
  return JSON.stringify(schema);
};

/**
 * Generate WebSite structured data (JSON-LD)
 */
export const generateWebSiteSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_CONFIG.name,
    alternateName: SITE_CONFIG.alternateName,
    url: SITE_CONFIG.url,
    description: SITE_CONFIG.description
  };
  
  return JSON.stringify(schema);
};

/**
 * Generate BreadcrumbList structured data (JSON-LD)
 */
export const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url
    }))
  };
  
  return JSON.stringify(schema);
};

/**
 * Generate CollectionPage structured data for category pages
 */
export const generateCollectionPageSchema = (category, categoryUrl, products) => {
  if (!category) return null;
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.name,
    description: category.description || generateCategoryDescription(category),
    url: categoryUrl,
    hasPart: products?.map(product => ({
      '@type': 'Product',
      name: product.name,
      url: `${SITE_CONFIG.url}/products/${product.id}`
    })) || []
  };
  
  return JSON.stringify(schema);
};

/**
 * Inject structured data into head
 */
export const injectStructuredData = (schema, index = 0) => {
  if (!schema) return null;
  
  return (
    <script
      key={`schema-${index}`}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: schema }}
    />
  );
};

/**
 * Generate image alt text
 */
export const generateImageAlt = (product, imageIndex = 0) => {
  if (!product) return 'Product image';
  
  const baseText = product.name;
  
  // Add color if available and this is not the main image
  if (product.colors && product.colors.length > 0 && imageIndex > 0) {
    const colorIndex = Math.min(imageIndex - 1, product.colors.length - 1);
    return `${baseText} in ${product.colors[colorIndex]}`;
  }
  
  return baseText;
};

/**
 * Check if page should be indexed
 */
export const shouldIndex = (pathname) => {
  const noindexPaths = [
    '/cart',
    '/checkout',
    '/profile',
    '/orders',
    '/wishlist',
    '/login',
    '/register'
  ];
  
  return !noindexPaths.some(path => pathname?.startsWith(path));
};

/**
 * Generate robots meta content
 */
export const generateRobotsMeta = (pathname) => {
  if (!shouldIndex(pathname)) {
    return 'noindex, nofollow';
  }
  return 'index, follow';
};

export default SITE_CONFIG;